import md5 from 'md5';
import dayjs from 'dayjs';
import { createInstance } from 'localforage';
import { isNumber } from 'lodash-es';

import { localAESStorage } from '@shared/utils/storage';
import { LSK } from '@shared/utils/storage/config';
import { IRequestParams } from './interface';

const EXPIRE_GAP = 5 * 60 * 1000;
const databaseInstance = createInstance({ name: 'a1.art', storeName: 'request_store' });

/**
 * 判断数据是否过期
 * @param time
 * @returns
 */
const isExpire = (time: number) => {
	if (!isNumber(time) || isNaN(time) || time < 0) {
		return true;
	}

	return dayjs(time).isBefore(Date.now());
};

/**
 * 遍历清除过期缓存
 */
const clearExpireCache = async () => {
	const keys = await databaseInstance.keys();

	Promise.allSettled(
		keys.map(async (key) => {
			const res = (await databaseInstance.getItem(key)) as Record<string, any>;

			if (res && isExpire(res.expire)) {
				return databaseInstance.removeItem(key);
			}
		}),
	).catch((err) => {
		console.error(err);
	});
};

const stringifyRequestParam = (...targets: any[]) => {
	try {
		const str = targets.reduce((prev, cur) => {
			return prev + JSON.stringify(cur);
		}, '');

		//让 key 的格式保持一致，如果后续需要拼接字段在 key 的前后会方便操作一些
		const res = md5(str);
		return res;
	} catch (error) {
		console.error(error);
		return '';
	}
};

/**
 * 设置请求缓存
 * @param param0
 * @param cacheData
 */
export const setRequestCache = async (
	{ url, data }: IRequestParams,
	cacheData: Record<string, any>,
) => {
	try {
		cacheData.expire = Date.now() + EXPIRE_GAP;

		const cacheKey = stringifyRequestParam(url, data);
		await databaseInstance.setItem(cacheKey, cacheData);
	} catch (error) {
		console.error(error);
	}
};

/**
 * 获取请求缓存
 * @param param0
 * @returns
 */
export const getRequestCache = async ({ url, data }: IRequestParams) => {
	const cacheKey = stringifyRequestParam(url, data);
	if (!cacheKey) return null;
	if ((window as any).clearingCache) return null;

	try {
		const res = (await databaseInstance.getItem(cacheKey)) as Record<string, any>;
		if (res && isExpire(res.expire)) {
			databaseInstance.removeItem(cacheKey);
			return null;
		}

		return res;
	} catch (error) {
		console.error(error);
		return null;
	}
};

export const removeRequestCache = async ({ url, data }: IRequestParams) => {
	const cacheKey = stringifyRequestParam(url, data);
	if (!cacheKey) return;

	try {
		await databaseInstance.removeItem(cacheKey);
	} catch (error) {
		console.error(error);
	}
};

export const getIsRefresh = () => {
	return !localAESStorage.getFlag(LSK.CACHE_REFRESH_TOKEN);
};

/**
 * 应用初始化
 */
export const initRequestExpireCache = () => {
	clearExpireCache();

	addEventListener('beforeunload', () => {
		// 十秒钟之内重新进入视为刷新
		localAESStorage.setFlag(LSK.CACHE_REFRESH_TOKEN, 10);
	});

	(window as any).clearingCache = false;
	if (getIsRefresh()) {
		(window as any).clearingCache = true;
		databaseInstance.clear().then(() => {
			(window as any).clearingCache = false;
		});
	}
};
